// src/pages/MentalPlus/IslandMap.tsx
import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import seaAnimation from "../../assets/MentalPlus/fish_only.json";
import styled from "styled-components";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useIonRouter, useIonViewWillEnter } from "@ionic/react";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import NextPrevButtons from "../../components/molecules/NextPrevButtons";
import Gap from "../../components/atoms/Gap";
import { ISLANDS, SUBISLANDS } from "./IslandRegistry";
import { useIslandVisibility } from "../../components/organisms/ResonanceContext/IslandVisibilityContext";
import { IslandTile, IslandWrapper, CenterIslandWrapper } from "./IslandTile";
import { useLongPressDelete } from "../../hooks/useLongPressDelete";
import { useIslandPositions } from "../../hooks/useIslandPositions";
import { RenameIslandModal } from "./RenameIslandModal";
import { useAuth } from "../../contexts/AuthContext";

const LONG_PRESS_MS = 500;

function getOrientationKey() {
  return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
}

const IslandMap = () => {
  const [selectedIsland, setSelectedIsland] = React.useState<string | null>(null);
  const [renameTarget, setRenameTarget] = React.useState<string | null>(null);

  const { top, bottom, center } = ISLANDS;
  const { visibility, setVisibility } = useIslandVisibility();

  const { setResonanceLayout } = useResonanceLayout();
  const { study } = useConfig();
  const ionRouter = useIonRouter();
  const { user } = useAuth();
  const userKey = user?.id ?? "anon";

  const {
    deleteTarget,
    setDeleteTarget,
    startLongPress,
    cancelLongPress,
    longPressTriggeredRef,
    squelchNextTapRef,
  } = useLongPressDelete(LONG_PRESS_MS);

  // ---- orientation / layout key ----
  const [layoutKey, setLayoutKey] = React.useState(getOrientationKey());

  React.useEffect(() => {
    const onResize = () => setLayoutKey(getOrientationKey());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize as any);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize as any);
    };
  }, []);

  // ---- positions (per user + per orientation) ----
  const scatter = false;
  const locstorage = true;
  const isDragActive = true;

  const { positions, getPos, setPosition, clearAllPositions } = useIslandPositions(
    `islandPositions_v1_${userKey}_${layoutKey}`,
    scatter,
    locstorage
  );

  // ---- refs ----
  const contentRef = React.useRef<HTMLDivElement>(null); // drag constraints + query tiles
  const scrollRef = React.useRef<HTMLDivElement>(null); // scroll container
  const draggingRef = React.useRef(false);

  // ---- dynamic scroll / padding-bottom ----
  const [extraBottom, setExtraBottom] = React.useState(0);
  const [scrollEnabled, setScrollEnabled] = React.useState(false);

  const measureOverflow = React.useCallback(() => {
    const scrollEl = scrollRef.current;
    const areaEl = contentRef.current;
    if (!scrollEl || !areaEl) return;

    const viewH = scrollEl.getBoundingClientRect().height;

    // Adjust if your bottom tab bar is taller/shorter
    const bottomSafe = 90;

    // IMPORTANT:
    // IslandTile outer wrapper must have class "island-tile" for this to work
    const tiles = areaEl.querySelectorAll<HTMLElement>(".island-tile");
    if (!tiles.length) {
      setExtraBottom(0);
      setScrollEnabled(false);
      scrollEl.scrollTop = 0;
      return;
    }

    const areaRect = areaEl.getBoundingClientRect();

    let maxBottomRel = 0;
    tiles.forEach((t) => {
      const r = t.getBoundingClientRect();
      const bottomRel = r.bottom - areaRect.top;
      if (bottomRel > maxBottomRel) maxBottomRel = bottomRel;
    });

    const usableH = viewH - bottomSafe;

    // how much content goes below the usable viewport
    const overflow = Math.max(0, Math.ceil(maxBottomRel - usableH));

    // add a little extra so user can drag slightly lower if needed
    const needed = overflow > 0 ? Math.min(overflow + 40, 420) : 0;

    setExtraBottom(needed);
    setScrollEnabled(needed > 0);

    // If no scroll needed, snap back to top (fixes “stuck scroll” after rotation)
    if (needed === 0) {
      scrollEl.scrollTop = 0;
    }
  }, []);

  // Re-measure when layout changes or islands move/appear/disappear
  React.useLayoutEffect(() => {
    // iOS layout can settle a frame later after rotation
    const r1 = requestAnimationFrame(() => {
      const r2 = requestAnimationFrame(() => {
        measureOverflow();
      });
      return () => cancelAnimationFrame(r2);
    });
    return () => cancelAnimationFrame(r1);
  }, [layoutKey, selectedIsland, visibility, positions, measureOverflow]);

  // Also reset scroll position on layout key changes (rotation)
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = 0;
  }, [layoutKey]);

  // Lottie speed
  const lottieRef = React.useRef<LottieRefCurrentProps>(null);
  React.useEffect(() => {
    lottieRef.current?.setSpeed(0.5);
  }, []);

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      welcomePage: false,
      title: "mentalplus_tab_home",
    });
  });

  // ---- navigation ----
  const handleIslandClick = (key: string) => {
    const visibleSubs = (SUBISLANDS[key] || []).filter((sub) => visibility[sub.key] !== false);
    if (visibleSubs.length === 0) {
      ionRouter.push(
        `/${study.slug}/tabs/home/island/${encodeURIComponent(key)}`,
        "none",
        "push"
      );
    } else {
      setSelectedIsland(key);
    }
  };

  const handleSubislandClick = (key: string) => {
    ionRouter.push(
      `/${study.slug}/tabs/home/island/${encodeURIComponent(key)}`,
      "none",
      "push"
    );
  };

  const handleBack = () => setSelectedIsland(null);

  // helper: visible keys signature
  const visibleKeysSig = React.useMemo(() => {
    const keys = [
      ...ISLANDS.top.filter((i) => visibility[i.key] !== false).map((i) => i.key),
      ...(visibility[ISLANDS.center.key] !== false ? [ISLANDS.center.key] : []),
      ...ISLANDS.bottom.filter((i) => visibility[i.key] !== false).map((i) => i.key),
    ];
    return keys.sort().join("|");
  }, [visibility]);

  const lastSigRef = React.useRef<string>("");

  React.useEffect(() => {
    if (lastSigRef.current && lastSigRef.current !== visibleKeysSig) {
      clearAllPositions();
    }
    lastSigRef.current = visibleKeysSig;
  }, [visibleKeysSig, clearAllPositions]);

  // ---- visible sets ----
  const visibleTop = top.filter((i) => visibility[i.key] !== false);
  const visibleBottom = bottom.filter((i) => visibility[i.key] !== false);
  const visibleCenter = visibility[center.key] !== false ? center : null;

  const visibleSubislands = selectedIsland
    ? (SUBISLANDS[selectedIsland] || []).filter((s) => visibility[s.key] !== false)
    : [];

  // ---- render row helper ----
  const renderRow = (
    items: typeof visibleTop,
    onOpen: (key: string) => void,
    WrapperComp: typeof IslandWrapper = IslandWrapper,
    draggable = true
  ) => (
    <IslandRow>
      {items.map((def) => (
        <IslandTile
          key={def.key}
          def={def}
          contentRef={contentRef}
          getPos={getPos}
          setPosition={setPosition}
          draggingRef={draggingRef}
          deleteTarget={deleteTarget}
          setDeleteTarget={setDeleteTarget}
          startLongPress={startLongPress}
          cancelLongPress={cancelLongPress}
          longPressTriggeredRef={longPressTriggeredRef}
          squelchNextTapRef={squelchNextTapRef}
          onOpen={onOpen}
          setVisibility={setVisibility}
          Wrapper={WrapperComp}
          draggable={draggable}
          onRename={(key) => {
            setRenameTarget(key);
            setDeleteTarget(null);
          }}
        />
      ))}
    </IslandRow>
  );

  return (
    <ResonanceLayout isConnected>
      <PageWrapper>
        <LottieBackground>
          <Lottie
            lottieRef={lottieRef}
            animationData={seaAnimation}
            loop
            autoplay
            style={{ width: "100%", height: "auto", objectFit: "fill" }}
          />
        </LottieBackground>

        <ScrollContainer
          ref={scrollRef}
          $scrollEnabled={scrollEnabled}
          onPointerDown={(e) => {
            if (!deleteTarget) return;
            const el = e.target as HTMLElement;
            if (el.closest(".delete-button")) return;
            if (el.closest(".hit-area")) return;
            setDeleteTarget(null);
          }}
        >
          <ConstraintArea ref={contentRef} key={layoutKey} $extraBottom={extraBottom}>
            <MainStage>
              <AnimatePresence mode="wait">
                {!selectedIsland ? (
                  <motion.div
                    key="main-islands"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.4 } }}
                    style={{ width: "100%" }}
                  >
                    <CenteringPad />
                    {renderRow(visibleTop, handleIslandClick, IslandWrapper, isDragActive)}
                    {visibleCenter &&
                      renderRow([visibleCenter], handleIslandClick, CenterIslandWrapper, isDragActive)}
                    {renderRow(visibleBottom, handleIslandClick, IslandWrapper, isDragActive)}
                  </motion.div>
                ) : (
                  <motion.div
                    key="subislands"
                    initial={{ opacity: 0, y: 60, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -60, scale: 0.9, transition: { duration: 0.3 } }}
                    style={{ width: "100%", height: "auto", maxHeight: "100%" }}
                  >
                    {renderRow(visibleSubislands, handleSubislandClick, IslandWrapper, isDragActive)}
                    <Gap size="70px" />
                    <NextPrevButtons onPreviousClick={handleBack} />
                  </motion.div>
                )}
              </AnimatePresence>
            </MainStage>
          </ConstraintArea>
        </ScrollContainer>

        <RenameIslandModal islandKey={renameTarget} onClose={() => setRenameTarget(null)} />
      </PageWrapper>
    </ResonanceLayout>
  );
};

export default IslandMap;

// ---------------- styles ----------------

const PageWrapper = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden; /* ✅ let ScrollContainer manage scroll */
`;

const LottieBackground = styled.div`
  position: fixed;
  z-index: 0;
  inset: 0;
  width: 100vw;
  height: 100dvh;
  min-height: 100vh;
  pointer-events: none;

  & > div {
    width: 100%;
    height: 100%;
  }

  svg,
  canvas {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const ScrollContainer = styled.div<{ $scrollEnabled: boolean }>`
  position: relative;
  z-index: 1;

  width: 100%;
  height: 100dvh;

  overflow-x: hidden;
  overflow-y: ${(p) => (p.$scrollEnabled ? "auto" : "hidden")};

  -webkit-overflow-scrolling: touch;
`;

const ConstraintArea = styled.div<{ $extraBottom: number }>`
  position: relative;
  min-height: 100dvh;

  /* ✅ only add extra space when needed */
  padding-bottom: ${(p) => `${p.$extraBottom}px`};
  box-sizing: border-box;

  touch-action: pan-y;
`;

/**
 * ✅ Fix "justified down":
 * Make the stage a normal column that starts at the top.
 * (No alignContent tricks / implicit centering.)
 */
const MainStage = styled.div`
  width: 100%;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

/**
 * ✅ Prefer center when there's room, but never force it on small screens.
 * (This is just a top spacer.)
 */
const CenteringPad = styled.div`
  height: clamp(0px, 4vh, 50px);
`;

const IslandRow = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  width: auto;
  margin: 0 auto;

  /* ✅ reduce the "pushed down" feeling */
  padding: 2vh 4vh 1vh 4vh;

  @media (max-height: 1000px) {
    padding: 2vh 4vh 1vh 4vh;
  }
  @media (max-height: 900px) {
    padding: 1.8vh 3vh 1vh 3vh;
  }
  @media (max-height: 800px) {
    padding: 1.5vh 3vh 1vh 3vh;
  }
  @media (max-height: 700px) {
    padding: 1vh 2vh 1vh 2vh;
  }

  @media (max-width: 900px) {
    padding: 2vh 4vh 1vh 4vh;
  }
  @media (max-width: 800px) {
    padding: 1.8vh 3vh 1vh 3vh;
  }
  @media (max-width: 700px) {
    padding: 1.5vh 2vh 1vh 2vh;
  }
  @media (max-width: 600px) {
    padding: 1vh 1vh 1vh 1vh;
  }
  @media (max-width: 500px) {
    padding: 8px;
  }
`;
